import {
  Brain,
  Camera,
  Sparkles,
  HeartPulse,
  Activity,
  LineChart,
  ShieldCheck,
  Pill,
  Droplet,
  CheckCircle2,
  Timer,
  ChefHat,
  Stethoscope,
  Mic,
  TestTube,
} from "lucide-react";

const features = [
  {
    Icon: Stethoscope,
    color: "text-brand-free",
    title: "Telehealth — on-demand medical guidance",
    body: "Tap a button. Get connected to a licensed clinician for instant medical advice — labs, prescriptions, symptom check, peptide & supplement consults. Available on every tier, from Free up.",
    span: "md:col-span-12",
    hero: true,
    badge: "All Tiers",
    badgeColor: "bg-brand-free/15 border-brand-free/40 text-brand-free",
  },
  {
    Icon: Brain,
    color: "text-brand-accent",
    title: "One AI brain, every signal",
    body: "Meals, training, sleep, peptides, supplements, wearables — all weighted against your personal goal and surfaced as a single live MIQ score.",
    span: "md:col-span-8",
    large: true,
    chips: ["nutrition", "recovery", "training", "biometrics"],
  },
  {
    Icon: Camera,
    color: "text-brand-genius",
    title: "4-way meal logging",
    body: "Photo AI plate scan, barcode, recent meals, or restaurant-aware manual — 500K+ menus already mapped. One second to log.",
    span: "md:col-span-4",
  },
  {
    Icon: Mic,
    color: "text-brand-smart",
    title: "🎙️ Voice logging — in any language",
    body: "Hold the mic. Say what you ate — in English, Spanish, French, Mandarin, Hindi, Arabic, or 50+ other languages. We auto-detect, transcribe with Whisper, and let you edit before it hits your log. Built for real life, not just English.",
    bullets: [
      "50+ languages auto-detected",
      "Edit transcripts before they log",
      "Works for meals AND Smart Coach questions",
      "Smart tier and up — unlimited on Genius",
    ],
    tierBadge: "Smart",
    tierBadgeColor: "bg-brand-smart/15 border-brand-smart/40 text-brand-smart",
    span: "md:col-span-6",
  },
  {
    Icon: ChefHat,
    color: "text-brand-free",
    title: "AI Recipe Generator",
    body: "Tap what's in your kitchen. Get 3 recipes built around your remaining macros — instantly.",
    span: "md:col-span-6",
  },
  {
    Icon: CheckCircle2,
    color: "text-brand-accent",
    title: "SmartMeals — 94% Perfect Fit",
    body: "AI scans meals that exactly match your remaining calorie + macro targets. No more guessing.",
    span: "md:col-span-4",
  },
  {
    Icon: Sparkles,
    color: "text-brand-genius",
    title: "Smart Swaps",
    body: "Greek Yogurt → Skyr. White Rice → Cauliflower. Real-time food substitutions that nudge MIQ up.",
    span: "md:col-span-4",
  },
  {
    Icon: Droplet,
    color: "text-brand-smart",
    title: "Micronutrient Intelligence",
    body: "10+ vitamins & minerals tracked live — Vit D, A, B12, Omega-3, Magnesium, Zinc, Calcium, Potassium and more.",
    span: "md:col-span-4",
  },
  {
    Icon: HeartPulse,
    color: "text-brand-free",
    title: "Daily Check-In",
    body: "30-second sleep / stress / energy / weight pulse that recalibrates your plan every morning.",
    span: "md:col-span-4",
  },
  {
    Icon: Timer,
    color: "text-brand-smart",
    title: "Fasting & Eating Windows",
    body: "Intermittent fasting, time-restricted eating, fed/fasted training — fully MIQ-aware.",
    span: "md:col-span-4",
  },
  {
    Icon: Pill,
    color: "text-brand-genius",
    title: "Peptide & Supplement Stack",
    body: "Log peptides, supplements, and meds. MIQ correlates them with biomarkers and adjusts the plan.",
    span: "md:col-span-4",
  },
  {
    Icon: TestTube,
    color: "text-brand-genius",
    title: "🩸 Bloodwork & Biomarkers — Genius",
    body: "Snap your lab PDF or photo. Our AI reads 15+ markers — lipids, glucose, inflammation, hormones, vitamins — and folds them into your MIQ Confidence score. See trends across draws, get plain-English \"why this matters\" on every flagged result, and let MIQ react to your actual blood, not a guess.",
    bullets: [
      "Multi-page PDF + multi-photo OCR",
      "15+ markers tracked across draws",
      "+15% MIQ Confidence boost on fresh labs",
      "Smart Coach insights driven by your bloodwork",
    ],
    tierBadge: "Genius",
    tierBadgeColor: "bg-brand-genius/15 border-brand-genius/40 text-brand-genius",
    span: "md:col-span-8",
  },
  {
    Icon: Activity,
    color: "text-brand-smart",
    title: "AI workout & rehab planner",
    body: "Personalized training, mobility, and rehab programming that rewrites itself as your MIQ, recovery, and biomarkers shift.",
    span: "md:col-span-4",
  },
  {
    Icon: LineChart,
    color: "text-brand-accent",
    title: "Weekly Intelligence Brief",
    body: "A 90-second read of what changed, what's working, and where to focus next. Sent every Sunday.",
    span: "md:col-span-4",
  },
  {
    Icon: ShieldCheck,
    color: "text-brand-free",
    title: "Your data, your brain",
    body: "Encrypted end-to-end. Never sold. Export everything anytime. You own the signal — we just run the intelligence.",
    span: "md:col-span-8",
    large: true,
  },
];

export default function FeatureGrid() {
  return (
    <section id="features" data-testid="features-section" className="relative py-28 md:py-36">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="text-[11px] uppercase tracking-[0.24em] text-brand-accent font-bold mb-5">What you get</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Built for <span className="text-gradient-hero">whoever you actually are</span>.
          </h2>
          <p className="mt-6 text-white/65 text-base sm:text-lg leading-relaxed font-light">
            Diabetic parent. Endurance athlete. Longevity biohacker. Wedding-prep dieter. Same brain,
            completely different output.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-12 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              data-testid={`feature-${i + 1}`}
              className={`glass rounded-3xl col-span-12 ${f.span} relative overflow-hidden ${
                f.hero ? "p-8 sm:p-10 border-2 border-brand-free/25 bg-gradient-to-br from-brand-free/[0.08] to-transparent" :
                f.large ? "p-8" : "p-6"
              }`}
            >
              {f.large && !f.hero && (
                <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full conic-ring opacity-12 blur-2xl" />
              )}
              {f.hero && (
                <>
                  <div className="absolute -top-28 -right-20 w-96 h-96 rounded-full bg-brand-free opacity-[0.08] blur-3xl" />
                  <div className="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-brand-accent opacity-[0.10] blur-3xl" />
                </>
              )}
              {f.badge && (
                <div className={`absolute top-6 right-6 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.2em] font-bold backdrop-blur-md ${f.badgeColor}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-free animate-glow-pulse" />
                  {f.badge}
                </div>
              )}
              {f.tierBadge && (
                <div className={`absolute top-6 right-6 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] uppercase tracking-[0.2em] font-bold backdrop-blur-md ${f.tierBadgeColor}`}>
                  {f.tierBadge}
                </div>
              )}
              <f.Icon className={`relative ${f.hero ? "w-9 h-9" : f.large ? "w-7 h-7" : "w-5 h-5"} ${f.color}`} />
              <h3 className={`relative mt-5 font-display font-bold tracking-tight ${
                f.hero ? "text-3xl sm:text-4xl max-w-2xl leading-[1.05]" :
                f.large ? "text-2xl" : "text-lg"
              }`}>{f.title}</h3>
              <p className={`relative mt-3 text-white/65 leading-relaxed ${
                f.hero ? "text-base sm:text-lg max-w-3xl font-light" :
                f.large ? "text-sm max-w-lg" : "text-xs"
              }`}>{f.body}</p>
              {f.bullets && (
                <ul className="relative mt-4 space-y-1.5">
                  {f.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-xs text-white/75 leading-snug">
                      <span className={`mt-1.5 h-1 w-1 rounded-full shrink-0 ${f.color.replace('text-', 'bg-')}`} />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
              {f.chips && (
                <div className="relative mt-6 flex items-center gap-4 text-xs text-white/55 font-mono flex-wrap">
                  {f.chips.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
                      {c}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
